import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  googleAuthApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Link } from '@backstage/core-components';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import HourglassIcon from '@material-ui/icons/HourglassEmptyOutlined';
import PauseIcon from '@material-ui/icons/PauseCircleOutline';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import { makeStyles } from '@material-ui/core/styles';
import { CALLER_ID_TOKEN_HEADER } from './callerToken';

export type StepStatus =
  | 'pending'
  | 'running'
  | 'awaiting_approval'
  | 'succeeded'
  | 'failed'
  | 'skipped';

export type RunStatus =
  | 'queued'
  | 'running'
  | 'awaiting_approval'
  | 'succeeded'
  | 'failed';

export interface Run {
  id: string;
  kind: string;
  env: string;
  target: string;
  actor: string;
  status: RunStatus;
  steps: {
    kind: string;
    title: string;
    status: StepStatus;
    detail?: string;
    externalUrl?: string;
  }[];
  pullRequests?: { repo: string; number: number; htmlUrl: string }[];
  followUps?: string[];
  liveUrl?: string;
  createdAt: string;
  updatedAt: string;
}

const useStyles = makeStyles(theme => ({
  step: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    padding: '6px 0',
  },
  title: { fontSize: '0.875rem' },
  detail: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    lineHeight: 1.5,
  },
  ok: { color: '#3fb950', flexShrink: 0 },
  running: { color: '#4c8dff', flexShrink: 0 },
  waiting: { color: '#d29922', flexShrink: 0 },
  failed: { color: '#f85149', flexShrink: 0 },
  pending: { color: 'var(--ar-fg-3)', flexShrink: 0 },
  section: { marginTop: 14 },
  sectionTitle: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--ar-fg-3)',
    marginBottom: 6,
  },
  followUp: { fontSize: '0.8125rem', lineHeight: 1.55, marginBottom: 6 },
  waitingBanner: {
    border: '1px solid var(--ar-border-soft)',
    borderLeft: '3px solid #d29922',
    borderRadius: 6,
    padding: '10px 12px',
    fontSize: '0.875rem',
    lineHeight: 1.55,
    marginBottom: 12,
  },
}));

const StepIcon = (props: {
  status: StepStatus;
  classes: Record<string, string>;
}) => {
  const { status, classes } = props;
  if (status === 'succeeded') {
    return <CheckCircleIcon fontSize="small" className={classes.ok} />;
  }
  if (status === 'failed') {
    return <ErrorIcon fontSize="small" className={classes.failed} />;
  }
  if (status === 'awaiting_approval') {
    return <PauseIcon fontSize="small" className={classes.waiting} />;
  }
  if (status === 'running') {
    return <HourglassIcon fontSize="small" className={classes.running} />;
  }
  return (
    <RadioButtonUncheckedIcon fontSize="small" className={classes.pending} />
  );
};

/**
 * One Control run, live.
 *
 * # Why this exists
 *
 * The backend has proxied `/api/control/runs/:id/stream` since the Control
 * plugin was written, and nothing consumed it — the SSE plumbing was built and
 * never wired to a screen. So a requester submitted a form and then had to go
 * and look at another product to find out whether anything happened.
 *
 * # Why the pause is rendered as a state and not as an error
 *
 * The most important thing this shows is `awaiting_approval`. A portal that
 * makes provisioning feel self-serve while a human gate is still in the middle
 * of it is a portal that teaches people the gate is a bug. It is not — it is
 * the design, and the panel says so in words.
 *
 * # Why fetch and not EventSource
 *
 * EventSource cannot set headers, and every route on the control router
 * requires the caller's Google ID token alongside the Backstage credential.
 */
export const RunPanel = (props: { runId: string; initial?: Run }) => {
  const classes = useStyles();
  const discovery = useApi(discoveryApiRef);
  const googleAuth = useApi(googleAuthApiRef);
  const identity = useApi(identityApiRef);
  const [run, setRun] = useState<Run | undefined>(props.initial);
  const [error, setError] = useState<string>();

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const [idToken, { token }] = await Promise.all([
          googleAuth.getIdToken(),
          identity.getCredentials(),
        ]);
        // Control decides whether this caller may read this run, so the token
        // is required — but an EMPTY one produces a 401 whose message reads as
        // a server fault rather than a sign-in problem. Say the true thing.
        if (!idToken) {
          throw new Error(
            'we could not read your Google identity, and a run is shown to the ' +
              'person who requested it',
          );
        }
        const baseUrl = await discovery.getBaseUrl('control');
        const response = await fetch(`${baseUrl}/runs/${props.runId}/stream`, {
          headers: {
            Accept: 'text/event-stream',
            [CALLER_ID_TOKEN_HEADER]: idToken,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
        });
        if (!response.ok || !response.body) {
          throw new Error(`${response.status} ${response.statusText}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          // SSE frames are separated by a blank line, and a chunk can split one
          // anywhere — including mid-JSON. Keep the tail for the next read.
          const frames = buffer.split('\n\n');
          buffer = frames.pop() ?? '';
          for (const frame of frames) {
            const data = frame
              .split('\n')
              .filter(line => line.startsWith('data:'))
              .map(line => line.slice(5).trim())
              .join('');
            if (!data) continue;
            try {
              const parsed = JSON.parse(data) as Run | { run?: Run };
              setRun((parsed as { run?: Run }).run ?? (parsed as Run));
            } catch {
              // A frame we cannot parse is not worth failing the panel over —
              // the next one supersedes it anyway.
            }
          }
        }
      } catch (e) {
        if (!controller.signal.aborted) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => controller.abort();
  }, [discovery, googleAuth, identity, props.runId]);

  if (!run) {
    return (
      <Typography className={classes.detail}>
        {error
          ? `Could not follow this run: ${error}. It is still running — open it in Control.`
          : 'Connecting…'}
      </Typography>
    );
  }

  return (
    <div>
      {run.status === 'awaiting_approval' && (
        <div className={classes.waitingBanner}>
          <strong>Waiting for a DevOps approval.</strong> This is the design,
          not a problem: everything so far has opened pull requests and run a
          read-only plan, and nothing in the cloud changes until somebody
          approves. Approving happens in{' '}
          <Link to={`https://control.answerrocket.com/pipelines/${run.id}`}>
            Control
          </Link>
          .
        </div>
      )}

      {run.steps.map(step => (
        <div className={classes.step} key={step.kind}>
          <StepIcon status={step.status} classes={classes} />
          <div>
            <div className={classes.title}>
              {step.externalUrl ? (
                <Link to={step.externalUrl}>{step.title}</Link>
              ) : (
                step.title
              )}
            </div>
            {step.detail && <div className={classes.detail}>{step.detail}</div>}
          </div>
        </div>
      ))}

      {!!run.pullRequests?.length && (
        <div className={classes.section}>
          <div className={classes.sectionTitle}>Pull requests</div>
          {run.pullRequests.map(pr => (
            <div className={classes.followUp} key={pr.htmlUrl}>
              <Link to={pr.htmlUrl}>
                {pr.repo}#{pr.number}
              </Link>
            </div>
          ))}
        </div>
      )}

      {!!run.followUps?.length && (
        <div className={classes.section}>
          {/* The part people miss. Secrets are created EMPTY and the first
              container crashes until they are filled in, and the DNS record is
              somebody's job. Saying so on the run, in the portal, is the whole
              reason this panel shows follow-ups rather than only steps. */}
          <div className={classes.sectionTitle}>Still yours to do</div>
          {run.followUps.map(item => (
            <div className={classes.followUp} key={item}>
              {item}
            </div>
          ))}
        </div>
      )}

      {run.liveUrl && (
        <div className={classes.section}>
          <Chip
            size="small"
            label={<Link to={run.liveUrl}>{run.liveUrl}</Link>}
          />
        </div>
      )}
    </div>
  );
};
